<template>
  <DashboardHeader title="Student Profile">
    <v-row v-if="studentNotFound">
      <vCardAlert
        title="No Student Data Found!"
        bgColor="warning"
        class="mx-auto my-15"
      ></vCardAlert>
    </v-row>

    <div v-else>
      <v-row>
        <v-col :cols="isMobile ? 12 : 3">
          <v-card
            :class="isMobile ? 'mx-2 mt-2' : 'mx-2 mt-2'"
            v-if="isStaff"
            shaped
            outlined
          >
            <v-list-item three-line>
              <SelectStudent
                @target-selected="getStudentProfile"
                :items="students"
              ></SelectStudent>
            </v-list-item>
          </v-card>

          <v-card
            class="mx-2 mt-4"
            shaped
            elevation="12"
            outlined
            v-if="student"
          >
            <v-list-item three-line>
              <v-list-item-content>
                <div class="overline mb-4">Student Profile</div>
                <v-btn
                  x-small
                  rounded
                  class="mt-1"
                  color="primary"
                  @click="openViewStatementDialog()"
                >
                  View Statement
                </v-btn>
                <v-list-item-title class="headline mb-1">{{
                  student.lname
                }}</v-list-item-title>
                <v-list-item-subtitle>{{ student.fname }}</v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-avatar tile size="80" :color="getThemeColor">
                <input
                  type="file"
                  id="files"
                  ref="fileInput"
                  style="display:none"
                  accept="image/*"
                  @change="getSelectedImage()"
                />
                <v-img
                  size="80"
                  :src="profilePic"
                  style="border-radius:50%"
                  @click="openImageDialog()"
                ></v-img>
              </v-list-item-avatar>
            </v-list-item>

            <v-list two-line>
              <v-list-item>
                <IDCardFrontSmall
                  v-if="student"
                  :student="student"
                ></IDCardFrontSmall>
              </v-list-item>

              <v-divider inset></v-divider>

              <v-list-item>
                <!-- <v-list-item-icon>
                  <v-icon color="indigo">mdi-vuetify</v-icon>
                </v-list-item-icon> -->
                <v-list-item-title>
                  <vCardContainer
                    class="pt-4"
                    :bgColor="getThemeColor"
                    :color="'white'"
                    :shaped="true"
                  >
                    <template #content>
                      <vInputField
                        :readonly="!isManager"
                        :defaultValue="studentProfile.finance_info.op"
                        label="Update Opening Balance"
                        :prefix="getCurrency"
                        @input-updated="updateOpeningBalance"
                        class="ml-4 mr-4 mt-2"
                      >
                      </vInputField>
                    </template>
                  </vCardContainer>
                </v-list-item-title>
              </v-list-item>

              <v-divider inset></v-divider>

              <v-list-item>
                <v-list-item-icon>
                  <v-icon color="indigo">mdi-credit-card-check</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title
                    >Current FID: {{ student.fid }}</v-list-item-title
                  >
                  <v-list-item-title
                    >Current Fees: {{ getCurrency
                    }}{{
                      studentProfile.finance_info.curfee
                    }}</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>

              <v-list-item>
                <v-list-item-icon>
                  <v-icon color="indigo">mdi-account</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title
                    >ID: {{ student.id_number }}</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>

              <v-list-item>
                <v-list-item-icon>
                  <v-icon color="indigo">mdi-cash</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>
                    <CreateTargetDiscount
                      :selectedStudentSid="student.sid"
                      @discount-created="getStudentDiscounts"
                    ></CreateTargetDiscount>
                    <ViewTargetDiscounts
                      :discounts="discounts"
                    ></ViewTargetDiscounts
                  ></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <v-card-actions>{{ student.comment }}</v-card-actions>
          </v-card>
        </v-col>
        <v-col :cols="isMobile ? 12 : 9">
          <v-card class="mt-2" shaped>
            <v-tabs
              v-model="tab"
              :background-color="getThemeColor"
              centered
              dark
              icons-and-text
            >
              <v-tabs-slider></v-tabs-slider>
              <v-tab href="#tab-1">
                Account
                <v-icon>mdi-cash-multiple</v-icon>
              </v-tab>
              <v-tab href="#tab-2" @click="getCardStatus()">
                Student ID
                <v-icon>mdi-card-account-details</v-icon>
              </v-tab>
              <v-tab href="#tab-3" @click="getUserProfile()">
                Profile
                <v-icon>mdi-account-box</v-icon>
              </v-tab>
              <v-tab href="#tab-5">
                Fee IDs & Amounts
                <v-icon>mdi-cash-multiple</v-icon>
              </v-tab>
              <v-tab href="#tab-4" @click="getUserProfile()">
                Academics
                <v-icon>mdi-chart-areaspline</v-icon>
              </v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab">
              <v-tab-item value="tab-1">
                <v-row v-if="student">
                  <v-col
                    :cols="isMobile ? 12 : 3"
                    class="px-2 mt-4"
                    v-for="(info, i) in financeTotal"
                    :key="i + Math.random()"
                  >
                    <vCardAlert
                      :bgColor="colors[i]"
                      :title="getCurrency + info.amount"
                      :shaped="true"
                      class="mx-2"
                      :text="info.description"
                    >
                    </vCardAlert>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    :cols="isMobile ? 12 : 4"
                    :class="isMobile ? 'mx-2 mt-4' : 'ml-2 mt-4'"
                  >
                    <v-card class="mt-4" v-if="student">
                      <v-toolbar color class="mb-1">
                        <v-toolbar-title
                          >Bills [{{ getCurrency }}
                          {{
                            studentProfile.finance_info
                              .future_billings_unpaid_sum
                          }}]</v-toolbar-title
                        >

                        <v-spacer></v-spacer>
                        <v-btn
                          @click="showBillingForm()"
                          dark
                          x-small
                          fab
                          right
                          v-if="isAdmin"
                          color="blue"
                          ><v-icon>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn
                          @click="showBillingList()"
                          dark
                          x-small
                          fab
                          right
                          v-if="isAdmin"
                          :color="getThemeColor"
                          class="ml-1"
                          ><v-icon>mdi-menu</v-icon></v-btn
                        >
                        <v-btn
                          @click="getCheckboxes()"
                          dark
                          x-small
                          fab
                          right
                          v-if="indices.length > 0 && isAdmin"
                          :color="getThemeColor"
                          class="ml-1"
                          >Pay</v-btn
                        >
                      </v-toolbar>

                      <RecordFutureBilling
                        class="mb-4"
                        v-if="showBillsForm"
                        :student="student"
                        @show-billing-list="showBillingList()"
                        @billing-recorded="refreshDataAfterBilling"
                      ></RecordFutureBilling>
                      <v-virtual-scroll
                        v-if="showBillsList"
                        :items="studentFutureBillings"
                        height="400"
                        item-height="100"
                      >
                        <template v-slot:default="props">
                          <v-list two-line>
                            <v-list-item :key="props.index + Math.random()">
                              <v-list-item-content>
                                <v-list-item-title
                                  v-text="props.item.description"
                                ></v-list-item-title>
                                <v-list-item-subtitle
                                  v-text="props.item.amount"
                                ></v-list-item-subtitle>
                                <v-list-item-subtitle
                                  v-text="props.item.date"
                                ></v-list-item-subtitle>
                              </v-list-item-content>
                              <v-list-item-action v-if="isAdmin">
                                <v-btn
                                  text
                                  color="success"
                                  @click="payFutureBill(props.index)"
                                  >Pay</v-btn
                                >
                                <v-checkbox
                                  v-model="indices"
                                  :value="props.index"
                                ></v-checkbox>
                              </v-list-item-action>
                            </v-list-item>
                            <v-divider></v-divider>
                          </v-list>
                        </template>
                      </v-virtual-scroll>
                    </v-card>
                  </v-col>

                  <v-col
                    :cols="isMobile ? 12 : 4"
                    :class="isMobile ? 'mx-2 mt-4' : 'ml-n2 mt-4'"
                  >
                    <v-card class="mt-4" v-if="student">
                      <v-toolbar
                        :style="{
                          'background-color': paymentBannerBg,
                          color: paymentBannerColor,
                        }"
                        class="mb-1"
                      >
                        <v-toolbar-title>Payments</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn
                          fab
                          color="primary"
                          x-small
                          @click="showPaymentForm()"
                          v-if="isAdmin"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn
                          fab
                          class="ml-1"
                          :color="getThemeColor"
                          dark
                          x-small
                          @click="showPaymentList()"
                          v-if="isAdmin"
                        >
                          <v-icon>mdi-menu</v-icon>
                        </v-btn>
                      </v-toolbar>
                      <RecordPayment
                        class="mb-4"
                        v-if="showForm"
                        :student="student"
                        @show-payment-list="showPaymentList"
                        @payment-recorded="refreshDataAfterPayment"
                      >
                      </RecordPayment>
                      <v-virtual-scroll
                        v-if="showList"
                        :items="studentPayments"
                        height="400"
                        item-height="100"
                      >
                        <template v-slot:default="props">
                          <v-list-item
                            :key="props.index + Math.random() + Math.random()"
                          >
                            <v-list-item-content>
                              <v-list-item-title>{{
                                props.item.description
                              }}</v-list-item-title>
                              <v-list-item-subtitle>{{
                                props.item.amount
                              }}</v-list-item-subtitle>
                              <v-list-item-subtitle>{{
                                props.item.entry_date
                              }}</v-list-item-subtitle>
                            </v-list-item-content>

                            <v-list-item-action>
                              <v-icon
                                @click="editPayment(props.index)"
                                v-if="isAdmin"
                                >mdi-pencil</v-icon
                              >
                              <v-icon
                                color="primary"
                                @click="viewPayment(props.item)"
                                >mdi-receipt</v-icon
                              >
                              <v-icon
                                color="red"
                                @click="deletePayment(props.index)"
                                v-if="isAdmin"
                                >mdi-delete</v-icon
                              >
                            </v-list-item-action>
                          </v-list-item>
                          <v-divider></v-divider>
                        </template>
                      </v-virtual-scroll>
                    </v-card>
                  </v-col>
                  <v-col
                    :cols="isMobile ? 12 : 4"
                    :class="isMobile ? 'mx-2 mt-4' : 'ml-n2 mt-4'"
                  >
                    <SMSIndividual
                      v-if="student"
                      class="mt-4"
                      :client="student"
                    ></SMSIndividual>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item value="tab-2">
                <v-row>
                  <div class="ml-10">
                    <IDCardFront
                      v-if="student"
                      :student="student"
                    ></IDCardFront>
                  </div>
                  <div v-if="student">
                    <v-switch
                      v-model="cardSwitch"
                      label="Print For Delivery"
                      @change="updateCardStatus()"
                    ></v-switch>
                  </div>
                </v-row>
              </v-tab-item>
              <v-tab-item value="tab-3">
                <v-layout row wrap>
                  <v-flex
                    md3
                    sm8
                    v-for="(dataArr, i) in studentData"
                    :key="i + Math.random() + Math.random()"
                    class="ml-8"
                  >
                    <v-card shaped class="mt-4">
                      <v-card-text
                        :style="{ 'backgroung-color': getThemeColor }"
                        >{{
                          dataArr[0].toUpperCase().replace("_", " ")
                        }}</v-card-text
                      >
                      <div>
                        <v-text-field
                          v-if="isAdmin"
                          class="mx-2"
                          :value="dataArr[1]"
                          @change="updateField(dataArr[0])"
                        ></v-text-field>
                        <span
                          v-else
                          class="mx-2"
                          @change="updateField(dataArr[0])"
                          >{{ dataArr[1] }}</span
                        >
                      </div>
                    </v-card>
                  </v-flex>
                </v-layout>
              </v-tab-item>
              <v-tab-item value="tab-4">
                <v-content class="ma-0 pa-0" v-if="student != null">
                  <ReportTabs></ReportTabs>
                </v-content>
              </v-tab-item>
              <v-tab-item value="tab-5">
                <TargetFIDTable
                  v-if="student"
                  :studentFeeIDs="studentFeeIDs"
                  :total="studentProfile.all_years_fees_total"
                  @fee-ids-deleted="refreshDataAfterPayment"
                ></TargetFIDTable>
                <!-- <v-content class="ma-0 pa-0" v-if="student != null">
                  <v-row no-gutters>
                    <v-col>
                      <div class="d-flex flex-row align-center">
                        <v-text-field
                          class="ml-6"
                          v-model="msg"
                          :placeholder="'Message...'+student.fname"
                          @keypress.enter="send"
                        ></v-text-field>
                        <v-btn
                          fab
                          dark
                          x-small
                          :color="getThemeColor"
                          class="ml-4 mr-4"
                          @click="send"
                        >
                          <v-icon>mdi-send</v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-content>
                <v-container class="fill-height" v-if="student != null">
                  <v-row class="fill-height pb-14" align="end">
                    <v-flex
                      v-scroll.self="onScroll"
                      max-height="400"
                      class="ml-6 mr-2 overflow-y-auto"
                      v-if="chat.length > 0"
                    >
                      <div
                        v-for="(item, index) in chat"
                        :key="index+Math.random()+Math.random()"
                        :class="['d-flex flex-row align-center my-2', item.from == 'user' ? 'justify-end': null]"
                      >
                        <v-card v-if="item.from == 'user'" color="#DCF8C6" class="pa-4" shaped>
                          <span v-if="item.from == 'user'" class="black--text mr-3">{{ item.msg }}</span>
                          <! <v-avatar :color="item.from == 'user' ? 'indigo': 'red'" size="36">
                          <span class="white--text">{{ item.from[0] }}</span>
                          </v-avatar>>
                        </v-card>

                        <v-card v-if="item.from != 'user'" color="#fff" class="pa-4" shaped>
                          <! <v-avatar :color="item.from == 'user' ? 'indigo': 'red'" size="36">
                          <span class="white--text">{{ item.from[0] }}</span>
                          </v-avatar>>
                          <span v-if="item.from != 'user'" class="black--text ml-3">{{ item.msg }}</span>
                        </v-card>
                      </div>
                    </v-flex>
                  </v-row>
                </v-container> -->
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
        <Loader></Loader>
        <div class="text-center">
          <v-dialog v-model="dialog" width="600" persistent>
            <v-card v-if="payBill">
              <div
                v-for="(bill, index) in bills"
                :key="index + Math.random() + Math.random()"
              >
                <v-card-title
                  :style="{ 'background-color': getThemeColor, color: 'white' }"
                  class="headline lighten-2"
                  >Paying For {{ bill.description }}</v-card-title
                >
                <v-card-title>Student ID: {{ bill.sid }}</v-card-title>
                <v-card-title>Amount: {{ bill.amount }}</v-card-title>
                <v-card-title>Class: {{ bill.bclass }}</v-card-title>
                <v-card-title>Date: {{ bill.date }}</v-card-title>
              </div>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="success" dark @click="payBillNow()"
                  >Pay Now</v-btn
                >
                <v-btn color="primary" dark @click="closeDialog()"
                  >Cancel</v-btn
                >
              </v-card-actions>
            </v-card>
            <v-card v-else-if="editPay">
              <v-card-title
                :style="{ 'background-color': getThemeColor, color: 'white' }"
                class="headline lighten-2"
                >Editing Payment Details For
                {{ editObj.description }}</v-card-title
              >
              <v-text-field
                class="mx-6"
                label="Student ID"
                :rules="rules"
                v-model="edSid"
                >Student ID: {{ editObj.sid }}</v-text-field
              >
              <v-text-field
                class="mx-6"
                label="Amount"
                :rules="rules"
                v-model="edAmount"
                >Amount: {{ editObj.amount }}</v-text-field
              >
              <v-select
                class="mx-6"
                :label="editObj.description.toString()"
                v-model="edDescription"
                item-text="name"
                item-value="id"
                :items="categories"
              ></v-select>
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="edDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    class="mx-6"
                    :rules="rules"
                    v-model="edDate"
                    label="Date"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    >{{ editObj.entry_date }}</v-text-field
                  >
                </template>
                <v-date-picker v-model="edDate" no-title scrollable>
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="menu = false"
                    >Cancel</v-btn
                  >
                  <v-btn text color="primary" @click="$refs.menu.save(edDate)"
                    >OK</v-btn
                  >
                </v-date-picker>
              </v-menu>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="success" dark @click="saveChanges()"
                  >Save Changes</v-btn
                >
                <v-btn color="primary" dark @click="closeDialog()"
                  >Cancel</v-btn
                >
              </v-card-actions>
            </v-card>
            <v-card v-else-if="deletePay">
              <v-card-title
                :style="{ 'background-color': getThemeColor, color: 'white' }"
                class="headline lighten-2"
                >Deleting Payment For {{ delObj.description }}</v-card-title
              >
              <v-card-title>Student ID: {{ delObj.sid }}</v-card-title>
              <v-card-title>Amount: {{ delObj.amount }}</v-card-title>
              <v-card-title>Ref. #: {{ delObj.rec_num }}</v-card-title>
              <v-card-title>Date: {{ delObj.entry_date }}</v-card-title>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="red" dark @click="deletePaymentNow(delObj)"
                  >Delete</v-btn
                >
                <v-btn color="primary" dark @click="closeDialog()"
                  >Cancel</v-btn
                >
              </v-card-actions>
            </v-card>
            <v-card v-else-if="viewStatementDialog">
              <v-card-title
                :style="{ 'background-color': getThemeColor, color: 'white' }"
                class="headline lighten-2"
                >Select Date and View Statement</v-card-title
              >
              <DatePickerWithMenu
                @date-selected="getStatementDateSelected"
              ></DatePickerWithMenu>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="red" dark @click="closeDialog()">Cancel</v-btn>
                <v-btn color="primary" dark @click="viewStudentStatement()"
                  >View Now</v-btn
                >
              </v-card-actions>
            </v-card>

            <v-card v-else-if="readComment">
              <v-card-title
                class="headline lighten-2"
                :style="{ 'background-color': getThemeColor, color: 'white' }"
              >
                Communication with {{ student.lname }} {{ student.fname }}
                <br />
                SID: {{ commentSid }}
                <br />
                Sent On: {{ commentDate }}
              </v-card-title>

              <v-card-text class="my-3">{{ commentMessage }}</v-card-text>

              <v-divider></v-divider>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" dark @click="closeDialog()">Close</v-btn>
              </v-card-actions>
            </v-card>
            <v-card v-else-if="bulkSMS">
              <v-card-title
                v-if="isAdmin"
                :style="{ 'background-color': getThemeColor, color: 'white' }"
                class="headline lighten-2"
                >Send Bulk SMS To Parents/Guardians Of Selected
                Students</v-card-title
              >
              <v-card-title
                v-else
                :style="{ 'background-color': getThemeColor, color: 'white' }"
                class="headline lighten-2"
                >Send Message To School Admin</v-card-title
              >

              <v-card-title class="mt-6 mx-4" v-if="isAdmin"
                >Number of Messages Left: {{ messageCredit }}</v-card-title
              >
              <v-card-title class="mt-6 mx-4" v-if="isAdmin"
                >Number of Selected Students: 1</v-card-title
              >
              <div v-if="isAdmin">
                <v-textarea
                  :rules="nameRules"
                  label="Message Body"
                  class="mx-10"
                  v-model="bulkSMSBody"
                  v-if="hasCredits"
                ></v-textarea>
                <v-alert
                  v-else
                  class="mx-6 mt-6"
                  color="red"
                  dark
                  rounded="pill"
                >
                  <h6>
                    Insufficient Credits : {{ messageCredit }} Messages Left
                  </h6>
                  <h6>Call or WhatsApp Kenneth on 076 243 9441</h6>
                  <h6>To Organize Credits</h6>
                </v-alert>
              </div>
              <div v-else>
                <v-textarea
                  :rules="nameRules"
                  label="Message Body"
                  class="mx-10"
                  v-model="bulkSMSBody"
                ></v-textarea>
              </div>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn rounded color="red" dark @click="closeDialog()"
                  >Cancel</v-btn
                >
                <v-btn
                  rounded
                  color="teal"
                  dark
                  @click="sendBulkSMSNow()"
                  v-if="hasCredits && isAdmin"
                  >Send Bulk SMS Now</v-btn
                >
                <v-btn
                  rounded
                  color="teal"
                  dark
                  @click="sendMessageNow()"
                  v-else
                  >Send Message Now</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>

        <!-- <v-btn
          v-if="selectedStudentSid != null && isAdmin"
          :color="getThemeColor"
          dark
          fab
          @click="noteDialog = !noteDialog"
          bottom
          right
          absolute
          class="mb-12"
          transition="slide-y-reverse-transition"
        >
          <v-icon>mdi-account-edit</v-icon>
        </v-btn> -->

        <!-- <v-row justify="center">
          <v-dialog v-model="noteDialog" persistent width="500">
            <v-card class="mr-2" width="500">
              <v-card-text>
                <DatePickerWithMenu @date-selected="getNoteDateSelected"></DatePickerWithMenu>

                <v-textarea rows="2" label="title" v-model="noteTitle" class="mt-2"></v-textarea>

                <v-textarea rows="7" label="Body" v-model="noteBody" class="mt-4"></v-textarea>
                <v-row>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="selectedNote !=null"
                    x-small
                    color="red"
                    @click="deleteNoteFromDialog()"
                    rounded
                    dark
                    class="mr-4"
                  >
                    <v-icon x-small>mdi-delete</v-icon>
                  </v-btn>
                  <v-btn
                    x-small
                    color="warning"
                    @click="closeNoteDialog()"
                    rounded
                    dark
                    class="mr-4"
                  >
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                  <v-btn x-small color="green" @click="saveNote()" rounded dark class="mr-4">
                    <v-icon x-small>mdi-check</v-icon>
                  </v-btn>
                </v-row>
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-row>

        <v-speed-dial
          v-if="selectedStudentSid != null && isAdmin"
          class="mr-16 mb-0"
          v-model="fab"
          bottom
          right
          absolute
          :direction="direction"
          :open-on-hover="hover"
          :transition="transition"
        >
          <template v-slot:activator>
            <v-btn v-model="fab" color="green accent-4" dark fab>
              <v-icon v-if="fab">mdi-close</v-icon>
              <v-icon v-else>mdi-clipboard-text</v-icon>
            </v-btn>
          </template>
          <v-btn
            rounded
            color="purple"
            small
            outlined
            class="mr-16"
            v-for="(note,i) in notes"
            :key="i+Math.random()"
          >
            <v-icon
              left
              color="green accent-4"
              class="mr-6"
              dark
              @click="readNote(note)"
            >mdi-android-messages</v-icon>
            {{note.title}}
            <v-icon left color="red" class="ml-6" dark @click="deleteNote(note)">mdi-delete</v-icon>
          </v-btn>
        </v-speed-dial> -->
      </v-row>
    </div>
  </DashboardHeader>
</template>

<script>
import students from "@/plugins/students";
import DashboardHeader from "@/components/DashboardHeader.vue";
import Loader from "@/components/Loader.vue";
import axios from "@/plugins/axios";
import ReportTabs from "@/components/SchoolAdmin/ReportTabs.vue";
import IDCardFront from "@/modules/IDCards/IDCardFront.vue";
import IDCardFrontSmall from "@/modules/IDCards/IDCardFrontSmall.vue";
import DatePickerWithMenu from "@/components/myLibrary/DatePickerWithMenu.vue";
import SMSIndividual from "@/modules/BulkSMS/SMSIndividual.vue";
import SelectStudent from "./SelectTarget.vue";
import RecordPayment from "./RecordPayment.vue";
import RecordFutureBilling from "./RecordFutureBilling.vue";
import vCardAlert from "@/components/Legos/vCardAlert.vue";
import TargetFIDTable from "./TargetFIDTable.vue";
import vInputField from "@/components/Legos/vInputField.vue";
import vCardContainer from "@/components/Legos/vCardContainer.vue";
import CreateTargetDiscount from "@/modules/TargetProfile/CreateTargetDiscount.vue";
import ViewTargetDiscounts from "@/modules/TargetProfile/ViewTargetDiscounts.vue";
export default {
  computed: {
    hasCredits() {
      return this.messageCredit >= 1;
    },
    getAllStudents() {
      return this.$store.getters.getAllStudents;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isMobile() {
      return this.$store.getters.isMobile;
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isStaff() {
      return this.$store.getters.isStaff;
    },
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    getCurrentStudent() {
      return this.$store.getters.getCurrentStudent;
    },

    getCurrentUser() {
      return this.$store.getters.getCurrentUser;
    },
    getUserSchool() {
      return this.$store.getters.getUserSchool;
    },
    getApiUrl() {
      return this.$store.getters.getApiUrl;
    },

    computedStudentProfile() {
      return this.studentProfile;
    },
  },

  mounted() {
    if (this.getCurrentStudent) {
      this.selectedStudentSid = this.getCurrentStudent.sid;
      this.getStudentMethod(this.selectedStudentSid);
    } else if (this.isStaff && this.$route.params.sid != undefined) {
      this.selectedStudentSid = this.$route.params.sid;
      this.getStudentMethod(this.selectedStudentSid);
    } else if (this.getCurrentUser && this.getCurrentUser.sid != "00-00-000") {
      this.selectedStudentSid = this.getCurrentUser.sid;
      this.getStudentMethod(this.selectedStudentSid);
    }
    this.getStudents();
  },
  components: {
    CreateTargetDiscount,
    ViewTargetDiscounts,
    vCardAlert,
    DashboardHeader,
    Loader,
    ReportTabs,
    IDCardFront,
    IDCardFrontSmall,
    DatePickerWithMenu,
    SMSIndividual,
    SelectStudent,
    RecordPayment,
    RecordFutureBilling,
    TargetFIDTable,
    vInputField,
    vCardContainer,
  },
  data: () => ({
    discounts: [],
    showForm: false,
    showList: true,
    showBillsForm: false,
    showBillsList: true,
    notes: [],
    noteID: 0,
    selectedNote: null,
    fab: false,
    direction: "top",
    hover: false,
    transition: "slide-y-reverse-transition",
    noteTitle: null,
    noteBody: null,
    cardSwitch: false,
    noteDate: new Date().toISOString().substr(0, 10),
    profilePic: null,
    nameRules: [(v) => !!v || "Field is required"],
    statementDateSelected: new Date().toISOString().substr(0, 10),
    students: [],
    selectedStudentSid: null,
    selectedImageName: null,
    selectedImage: null,
    selectedFolder: "students_profile_pics",
    studentProfile: null,
    scrollInvoked: 0,
    chat: [],
    msg: null,
    menu: false,
    student: null,
    categories: [],
    commentMessage: "",
    commentSid: "",
    commentDate: "",
    noteDialog: false,
    dialog: false,
    addPay: false,
    readComment: false,
    paymentBanner: "All Payments",
    paymentBannerColor: "",
    paymentBannerBg: "",
    bills: [],
    indices: [],
    delObj: {},
    payBill: false,
    editPay: false,
    editObj: {},
    edSid: null,
    edAmount: null,
    edDescription: null,
    edDate: null,
    rules: [(value) => !!value || "Required!!"],
    deletePay: false,
    colors: ["#000", "warning", "red", "teal", "purple", "#3E2723"],
    icons: ["mdi-content-save", "mdi-credit-card"],
    financeTotal: [
      { description: "Opening Balance", amount: 0 },
      { description: "Previous Due", amount: "" },
      { description: "Due Now", amount: "" },
      { description: "Total Due", amount: "" },
    ],
    selected: [1],
    tab: null,
    studentNotFound: false,
    studentFinance: null,
    studentPayments: [],
    studentData: [],
    studentFutureBillings: [],
    guardian: null,
    statementComments: [],
    messageCredit: null,
    bulkSMS: false,
    bulkSMSBody: null,
    snackbarModel: false,
    snackbarText: null,
    snackbarColor: null,
    viewStatementDialog: false,
    studentFeeIDs: [],
  }),
  methods: {
    getStudentDiscounts(discounts) {
      this.discounts = discounts;
    },
    updateOpeningBalance(finalVal) {
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/update-opening-balance",
          {
            sid: this.student.sid,
            finalVal,
          },
          this.getAccessToken
        )
        .then((response) => {
          if (response == "success") {
            this.getStudentMethod(this.student.sid);
            this.$toast.success("Opening Balance Updated Successfully!");
          } else {
            this.$toast.error("Oops, Something Went Wrong!");
          }
          this.$store.commit("setLoader", false);
        });
    },
    async getStudents() {
      if (this.isStaff) {
        const studentsArr = await students.getStudents();
        studentsArr.forEach((student) => {
          this.students.push({
            text:
              student.lname + " " + student.fname + " [ " + student.sid + " ]",
            value: student.sid,
          });
        });
      }
    },
    openImageDialog() {
      if (this.getCurrentUser.email == "adubyte2@yahoo.com") {
        document.getElementById("files").click();
      }
    },
    getSelectedImage() {
      const file = event.target.files[0];
      const ext = file.type.split("/")[1];
      this.selectedImageName = this.student.sid + "." + ext;
      const fileReader = new FileReader();
      fileReader.addEventListener("load", () => {
        this.selectedImage = fileReader.result;
        this.$store.commit("setLoader", true);
        axios
          .postDataWithToken(
            "/auth/upload-image",
            {
              image64: this.selectedImage,
              name: this.selectedImageName,
              folder: this.selectedFolder,
              selectedClass: this.student.class,
            },
            this.getAccessToken
          )
          .then(() => {
            this.profilePic = null;
            this.$store.commit("setLoader", false);
            const img =
              this.getApiUrl +
              "/images/students_profile_pics/" +
              this.getUserSchool.emis +
              "/" +
              this.student.sid +
              ".jpeg";

            this.profilePic = img;
          });
      });
      fileReader.readAsDataURL(file);
    },
    openBulkSMSDialog() {
      if ([this.selectedStudentSid].length < 1) {
        alert("You Must Select At least One(1) Student!");
        event.preventDefault();
      } else {
        this.$store.commit("setLoader", true);
        axios
          .getData("/auth/get-messages-credit", this.getAccessToken)
          .then((credit) => {
            this.messageCredit = credit;
            this.$store.commit("setLoader", false);
          });
        this.dialog = true;
        this.bulkSMS = true;
      }
    },
    openViewStatementDialog() {
      this.dialog = true;
      this.viewStatementDialog = true;
    },
    getStatementDateSelected(selectedDate) {
      this.statementDateSelected = selectedDate;
    },
    viewStudentStatement() {
      if ([this.selectedStudentSid].length < 1) {
        alert("You Must Select At least One(1) Student!");
        event.preventDefault();
        return;
      }

      this.$router.push({
        name: "students-statements",
        params: {
          selectedStudents: [this.selectedStudentSid],
          statementsDate: this.statementDateSelected,
        },
      });
    },
    send() {
      // axios.postDataWithToken('/auth/send-message',{message:,sender:,receiver,threadId,})
      this.chat.push({
        from: "user",
        msg: this.msg,
      });
      this.msg = null;
      this.addReply();
    },
    addReply() {
      this.chat.unshift({
        from: "Admin",
        msg: "Thanks for reaching out to us, we will get back to you shortly",
      });
    },
    getUserProfile() {
      if (this.student != null) {
        this.studentData = [];
        const studentData = Object.entries(this.student);
        studentData.forEach((dataArr) => {
          if (
            dataArr[0] != "id" &&
            dataArr[0] != "emis" &&
            !dataArr[0].includes("sub_") &&
            !dataArr[0].includes("_at")
          ) {
            this.studentData.push(dataArr);
          }
        });
      }
    },
    updateField(column) {
      const value = event.target.value;
      axios
        .postDataWithToken(
          "/auth/update-students-table-data",
          { column, value, id: this.student.id },
          this.getAccessToken
        )
        .then((student) => {
          this.student = student;
          this.getUserProfile();
        });
    },
    editPayment(index) {
      this.dialog = true;
      this.editPay = true;
      this.$store.commit("setLoader", true);
      this.editObj = this.studentPayments[index];
      this.edDescription = this.studentPayments[index].description;
      this.edAmount = this.studentPayments[index].amount;
      this.edSid = this.studentPayments[index].sid;
      this.edDate = this.studentPayments[index].entry_date;
      axios
        .getData("/auth/get-all-income-categories", this.getAccessToken)
        .then((categories) => {
          this.$store.commit("setLoader", false);
          this.categories = categories;
        });
    },
    saveChanges() {
      this.editObj.sid = this.edSid;
      this.editObj.description = this.edDescription;
      this.editObj.amount = this.edAmount;
      this.editObj["entry_date"] = this.edDate;
      this.$store.commit("setLoader", true);

      axios
        .postDataWithToken(
          "/auth/edit-payment",
          { editObj: this.editObj },
          this.getAccessToken
        )
        .then((payments) => {
          this.getStudentMethod(this.edSid);
          this.$store.commit("setLoader", false);
          this.studentPayments = payments;
          this.$toast.success("Payment Updated Successfully!", {
            duration: 3000,
          });
          this.$store.commit("setLoader", false);
          this.showPaymentList();
        });
    },
    viewPayment(item) {
      this.$router.push({
        name: "payments-receipt",
        params: {
          student: this.student,
          payment: item,
          studentFinance: this.studentFinance,
        },
      });
    },
    deletePayment(index) {
      this.dialog = true;
      this.deletePay = true;
      this.delObj = this.studentPayments[index];
    },
    deletePaymentNow(delObj) {
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken("/auth/delete-payment", delObj, this.getAccessToken)
        .then((payments) => {
          this.studentPayments = payments;
          this.$store.commit("setLoader", false);
          this.getStudentMethod(delObj.sid);
          this.showPaymentList();
          this.$toast.success("Payment Successfully Deleted!", {
            duration: 3000,
          });
        });
    },
    payFutureBill(index) {
      this.dialog = true;
      this.payBill = true;
      this.bills.push(this.studentFutureBillings[index]);
    },
    payBillNow() {
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/pay-future-bills",
          { bills: this.bills },
          this.getAccessToken
        )
        .then((payments) => {
          this.bills.forEach((bill) => {
            this.studentFutureBillings.splice(
              this.studentFutureBillings.indexOf(bill),
              1
            );
          });
          this.$store.commit("setLoader", false);
          this.studentPayments = payments;
          this.$toast.success("Payments Recorded Successfully!", {
            duration: 3000,
          });
          this.$store.commit("setLoader", false);
          this.showPaymentList();
        });
    },
    getCheckboxes() {
      this.indices.forEach((index) => {
        this.bills.push(this.studentFutureBillings[index]);
      });
      this.dialog = true;
      this.payBill = true;
    },
    closeDialog() {
      this.addPay = false;
      this.dialog = false;
      this.payBill = false;
      this.editPay = false;
      this.deletePay = false;
      this.readComment = false;
      this.bills = [];
      this.indices = [];
      this.bulkSMS = false;
      this.viewStatementDialog = false;
    },
    termDataAvailable() {
      return true;
    },
    getStudentProfile(selectedStudentSid) {
      this.selectedStudentSid = selectedStudentSid;
      this.getStudentMethod(selectedStudentSid);
    },
    getStudentMethod(sid) {
      this.cardSwitch = false;
      this.$store.commit("setLoader", true);
      // axios
      //   .postDataWithToken("/auth/get-notes", { sid }, this.getAccessToken)
      //   .then(notes => {
      //     this.notes = notes;
      //     this.closeNoteDialog();
      //   });
      axios
        .postDataWithToken(
          "/auth/get-student-profile",
          { sid },
          this.getAccessToken
        )
        .then((studentProfile) => {
          if (studentProfile == "not found") {
            this.$store.commit("setLoader", false);
            if (!this.isStaff) {
              this.studentNotFound = true;
            }
          } else {
            this.studentNotFound = false;
            this.studentProfile = studentProfile;
            this.student = studentProfile.student;
            this.discounts = studentProfile.discounts;
            this.$store.commit("setCurrentStudent", studentProfile.student);
            this.$store.commit("setCurrentStudentProfile", studentProfile);
            this.studentFinance = studentProfile.finance_info;
            this.studentFutureBillings =
              studentProfile.finance_info.future_billings;
            this.studentFeeIDs = studentProfile.fee_ids;
            this.studentPayments = studentProfile.payments;
            this.guardian = studentProfile.guardian;
            this.statementComments = studentProfile.statement_comments;
            this.financeTotal[0].description =
              studentProfile.finance_info.comment.split(" ")[0] + " B/F";
            this.financeTotal[0].amount = studentProfile.finance_info.op;
            this.financeTotal[1].amount =
              studentProfile.finance_info.expected_paid_excluding_current_month;
            this.financeTotal[2].amount =
              studentProfile.finance_info.balance_due_month;
            this.financeTotal[3].amount =
              studentProfile.finance_info.balance_due_year;
            this.getUserProfile();
            this.$store.commit("setLoader", false);
            const img =
              this.getApiUrl +
              "/images/students_profile_pics/" +
              this.getUserSchool.emis +
              "/" +
              this.studentProfile.student.sid +
              ".jpeg";

            this.profilePic = img;
          }
          this.getCardStatus();
        });
    },
    onScroll() {
      this.scrollInvoked++;
    },
    readCommentNow(comment) {
      this.dialog = true;
      this.readComment = true;
      this.commentMessage = comment.body;
      this.commentSid = this.selectedStudentSid || this.getCurrentUser.sid;
      this.commentDate = comment.time_stamp;
    },
    sendBulkSMSNow() {
      const bulkSMSBody = this.bulkSMSBody;
      const students = [this.selectedStudentSid];
      if ([this.selectedStudentSid].length > 0 && bulkSMSBody != null) {
        this.$store.commit("setLoader", true);
        axios
          .postDataWithToken(
            "/auth/send-bulk-sms",
            { bulkSMSBody, students },
            this.getAccessToken
          )
          .then((messages) => {
            if (messages.length && messages.length > 0) {
              axios
                .postDataWithToken(
                  "/auth/update-messages-table-with-sent-messages",
                  { messages, origin: "student-profile" },
                  this.getAccessToken
                )
                .then((messages) => {
                  this.statementComments = messages;
                  this.$store.commit("setLoader", false);
                  this.$toast.success("Messages Successfully Sent!", {
                    duration: 3000,
                  });
                });
            } else {
              this.$store.commit("setLoader", false);
              this.$toast.error("Oops, Something Went Wrong!", {
                duration: 3000,
              });
            }
            this.closeDialog();
          });
      } else {
        alert("Message Body Field is Required!");
        event.preventDefault();
      }
    },
    sendMessageNow() {
      const bulkSMSBody = this.bulkSMSBody;
      const senderId = this.getCurrentUser.sid;
      if (bulkSMSBody != null) {
        this.$store.commit("setLoader", true);
        axios
          .postDataWithToken(
            "/auth/save-student-parent-messages",
            { bulkSMSBody, senderId },
            this.getAccessToken
          )
          .then((messages) => {
            this.statementComments = messages;
            this.$store.commit("setLoader", false);
            this.snackbarText = "Messages Successfully Sent!";
            this.snackbarColor = "success";
            this.snackbarModel = true;
          });
        this.closeDialog();
      } else {
        alert("Message Body Field is Required!");
        event.preventDefault();
      }
    },
    getNoteDateSelected(selectedDate) {
      this.noteDate = selectedDate;
    },
    saveNote() {
      if (this.noteTitle == null || this.noteBody == null) {
        alert("Make Sure Note Title And Body Are Not Empty");
        event.preventDefault();
      } else {
        this.$store.commit("setLoader", true);
        const data = {
          title: this.noteTitle,
          body: this.noteBody,
          sid: this.selectedStudentSid,
          date: this.noteDate,
          id: this.noteID,
        };

        axios
          .postDataWithToken("/auth/save-note", data, this.getAccessToken)
          .then((notes) => {
            this.notes = notes;
            this.$store.commit("setLoader", false);
            this.closeNoteDialog();
          });
      }
    },
    readNote(note) {
      this.noteTitle = note.title;
      this.noteBody = note.body;
      this.noteDate = note.date;
      this.noteID = note.id;
      this.noteDialog = true;
      this.selectedNote = note;
    },
    deleteNote(note) {
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/delete-note",
          { id: note.id, sid: this.selectedStudentSid },
          this.getAccessToken
        )
        .then((notes) => {
          this.notes = notes;
          this.$store.commit("setLoader", false);
          this.closeNoteDialog();
        });
    },
    deleteNoteFromDialog() {
      this.deleteNote(this.selectedNote);
    },
    closeNoteDialog() {
      this.noteTitle = null;
      this.noteBody = null;
      this.noteDate = new Date().toISOString().substr(0, 10);
      this.noteID = 0;
      this.selectedNote = null;
      this.noteDialog = false;
    },
    updateCardStatus() {
      if (
        this.cardSwitch &&
        this.getCurrentUser.email == "adubyte2@yahoo.com"
      ) {
        this.$store.commit("setLoader", true);
        axios
          .postDataWithToken(
            "/auth/update-card-status",
            { sid: this.student.sid },
            this.getAccessToken
          )
          .then(() => {
            this.$store.commit("setLoader", false);
          });
      }
    },
    showBillingForm() {
      this.closeDialog();
      this.showBillsForm = true;
      this.showBillsList = false;
    },
    showBillingList() {
      this.closeDialog();
      this.showBillsList = true;
      this.showBillsForm = false;
    },
    showPaymentForm() {
      this.closeDialog();
      this.showForm = true;
      this.showList = false;
    },
    showPaymentList() {
      this.closeDialog();
      this.showForm = false;
      this.showList = true;
    },
    refreshDataAfterPayment() {
      this.getStudentMethod(this.student.sid);
      this.showPaymentList();
    },
    refreshDataAfterBilling() {
      this.getStudentMethod(this.student.sid);
      this.showBillingList();
    },
    getCardStatus() {
      if (
        this.student &&
        this.student.sid &&
        this.student.sid != null &&
        this.student.sid != undefined
      ) {
        this.$store.commit("setLoader", true);
        axios
          .postDataWithToken(
            "/auth/get-card-status",
            { sid: this.student.sid },
            this.getAccessToken
          )
          .then((status) => {
            if (status == "present") {
              this.cardSwitch = true;
            } else {
              this.cardSwitch = false;
            }

            this.$store.commit("setLoader", false);
          });
      }
    },
  },
};
</script>

<style></style>
